import { AdaptedCreditOffersResponse, ApiCreditOffersResponse } from 'types/CreditOfferTypes';
import adaptCreditOffers from 'adaptors/creditOffers/credit-offer-adaptor';
import LoggerFactory from 'utilities/loggerv2/loggerFactory';
import ErrorCodes from 'utilities/loggerv2/errorCodes';
import { WIREMOCK_HOST } from 'constants/constant-urls';

class CreditOffersService {
  private host: string;

  private timeout: number;

  private apiPath: string;

  private mockMode: boolean;

  private readonly logger = LoggerFactory.getLogger('credit-offers-service');

  constructor(mockMode = false, host = '', apiPath = '/api/v1/credit-offers/product/offers') {
    this.mockMode = mockMode;
    this.host = this.mockMode ? WIREMOCK_HOST : host;
    this.apiPath = apiPath;
    this.timeout = 1000;
  }

  public async getCreditOffers(
    queryParams: {
      price: number;
      catalogueId: string;
      isBnplEligible: boolean;
    },
    options: {
      headers?: Record<string, string>;
    } = {},
  ): Promise<AdaptedCreditOffersResponse | void> {
    const { price, catalogueId, isBnplEligible } = queryParams;

    try {
      if (!price && !this.mockMode) throw new Error('Price was not provided');

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const url = this.mockMode
        ? `${this.host}${this.apiPath}`
        : `${this.host}${this.apiPath}?price=${price}&catalogueId=${catalogueId}&isBnplEligible=${isBnplEligible}`;

      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/vnd.api+json',
          ...options?.headers,
        },
        method: 'GET',
      });

      clearTimeout(timeoutId);

      const data = (await response.json()) as ApiCreditOffersResponse;
      const adaptedResponse = adaptCreditOffers(data?.data?.attributes);

      return adaptedResponse;
    } catch (error) {
      try {
        // Create a proper Error object with code
        const errorObj = error instanceof Error ? error : new Error(String(error || 'Unknown error'));

        // Safely add code property if it doesn't exist
        const hasCodeProperty =
          Object.prototype.hasOwnProperty.call(errorObj, 'code') &&
          (errorObj as any).code !== undefined &&
          (errorObj as any).code !== null;

        if (!hasCodeProperty) {
          Object.defineProperty(errorObj, 'code', {
            value: 'UNKNOWN_ERROR',
            writable: true,
            enumerable: true,
          });
        }

        // Verify error code before logging
        try {
          const logger = await this.logger;
          if (logger && typeof logger.logWithCode === 'function') {
            const errorCode = ErrorCodes.CREDIT_OFFERS_API_REQUEST_ERROR;
            if (errorCode && typeof errorCode === 'object' && 'code' in errorCode) {
              logger.logWithCode(errorCode, errorObj);
            } else {
              // Fallback if error code is invalid
              logger.error('Credit offers API request error (fallback)', errorObj, {
                errorCategory: 'CREDIT_OFFERS',
                errorCode: 'CREDIT_OFFERS_API_REQUEST_ERROR',
              });
            }
          } else {
            console.error('Logger not available for credit offers error:', errorObj);
          }
        } catch (logError) {
          console.error('Error during credit offers logging:', logError);
          console.error('Original error:', errorObj);
        }

        // You could add APM span recording here if needed

        return undefined;
      } catch (fatalError) {
        // Last resort fallback
        console.error('Critical error in credit offers error handler:', fatalError);
        return undefined;
      }
    }
  }
}

export default CreditOffersService;
